/**
 * useExportacoes (M9) — dados da tela "Exportações" sobre React Query.
 *
 * - `useListaExportacoes`: GET /exportacoes; refetch automático (polling 2s)
 *   ENQUANTO houver item em `na_fila`/`gerando` (RF-M9-006 AC2). Quando todos
 *   estiverem em status terminal (`pronta`/`expirada`/`falha` — contrato), o
 *   polling para (refetchInterval = false).
 * - `useCriarExportacao`: POST /exportacoes; invalida a lista ao concluir.
 * - `useBaixarExportacao`: GET /exportacoes/{id}/download e abre a URL.
 *
 * Nenhum dado pessoal vai a console/analytics (RN-M11-001).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import {
  CriarExportacaoInput,
  ExportacaoListItem,
  STATUS_TERMINAIS,
} from '../services/exportacoes-types';
import { PaginaExportacoes } from '../services/exportacoesApi';

const POLL_MS = 2000;

/** True se há alguma exportação ainda em andamento (na_fila/gerando). */
export function temPendente(itens: readonly ExportacaoListItem[]): boolean {
  return itens.some((e) => !STATUS_TERMINAIS.has(e.status));
}

export interface EstadoExportacoes {
  readonly itens: readonly ExportacaoListItem[];
  readonly carregando: boolean;
  readonly erro: boolean;
  readonly temPendente: boolean;
  recarregar(): void;
}

/** Lista as exportações do usuário, com polling enquanto houver pendente. */
export function useListaExportacoes(): EstadoExportacoes {
  const { exportacoes } = useServices();
  const query = useQuery<PaginaExportacoes>({
    queryKey: ['exportacoes'],
    queryFn: () => exportacoes.listar(),
    // Polling só enquanto houver item em andamento (para quando todos terminam).
    refetchInterval: (q) => {
      const dados = q.state.data as PaginaExportacoes | undefined;
      return dados && temPendente(dados.itens) ? POLL_MS : false;
    },
  });
  const itens = query.data?.itens ?? [];
  return {
    itens,
    carregando: query.isLoading,
    erro: query.isError,
    temPendente: temPendente(itens),
    recarregar: () => void query.refetch(),
  };
}

/** Mutação de criação (POST /exportacoes); invalida a lista ao concluir. */
export function useCriarExportacao(): {
  criar(input: CriarExportacaoInput): Promise<void>;
  enviando: boolean;
  erro: boolean;
} {
  const { exportacoes } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: CriarExportacaoInput) => exportacoes.criar(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exportacoes'] });
    },
  });
  return {
    criar: async (input) => {
      await mutation.mutateAsync(input);
    },
    enviando: mutation.isPending,
    erro: mutation.isError,
  };
}

/** Ação de download: obtém a URL pré-assinada e a abre em nova aba. */
export function useBaixarExportacao(): {
  baixar(id: string): Promise<void>;
  baixando: boolean;
} {
  const { exportacoes } = useServices();
  const mutation = useMutation({
    mutationFn: (id: string) => exportacoes.baixar(id),
    onSuccess: (res) => {
      // Abre a URL pré-assinada; o navegador inicia o download.
      window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });
  return {
    baixar: async (id) => {
      await mutation.mutateAsync(id);
    },
    baixando: mutation.isPending,
  };
}
